/* eslint-disable no-underscore-dangle */
import { ProcessRunState } from '@robocorp/robocloud-orchestrator-interface';
import { Cursor, ProcessRun } from '@robocorp/robocloud-search-iternal-sdk';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { SortDirection } from '@robocorp/components/dist/components/Table';
import { validateApiResponse } from '../utils';
import {
  getSearchProcessRuns,
  getSearchProcessRunsByProcess,
  getSearchProcessRunsWithClosestRunNumber,
  IndexedProcessRunsQueryArgs,
} from '../../services/searchAPI';

export interface IndexedProcessRunsQueryState {
  processRuns: ProcessRun[];
  total: number;
}

interface CursorIndexedProcessRunsQueryState extends IndexedProcessRunsQueryState {
  nextCursor: Cursor;
  previousCursor: Cursor;
}

interface QueryKeyArgs {
  workspaceId: string;
  processId?: string;
  sortBy?: string;
  sortOrder?: SortDirection;
  search?: string;
  from?: number;
  size?: number;
  cursor?: Cursor;
  // if is passed the results will be filtered by the target state
  // will return all processruns if not defined
  processState?: ProcessRunState;
}

interface ClosestRunsQueryKeyArgs {
  workspaceId: string;
  processId: string;
  runNo: number;
}

export const getIndexedProcessRunsQueryKey = ({
  workspaceId,
  processId,
  sortBy,
  sortOrder,
  search,
  from,
  size,
  processState,
  cursor,
}: QueryKeyArgs) => [
  'indexedProcessRuns',
  { workspaceId, processState },
  { processId, sortBy, sortOrder, search, from, size, cursor },
];

export const indexedProcessRunsQueryUpdater =
  (processRun: ProcessRun) =>
  (state?: IndexedProcessRunsQueryState): IndexedProcessRunsQueryState => {
    if (!state) return { processRuns: [], total: 0 };
    // we can assume that the IndexedProcessRun is the same as the incoming update
    // because IndexedProcessRun is the the same update event indexed in ES
    const idx = state?.processRuns.findIndex((prun) => prun.id === processRun.id);
    if (idx === -1) return state;

    const processRuns = [...state.processRuns];
    processRuns[idx] = processRun;

    return { ...state, processRuns };
  };

export const useIndexedProcessRunsQuery = ({
  workspaceId,
  processId,
  sortBy,
  sortOrder,
  search,
  from,
  cursor,
  processState,
  size,
  options = {},
}: QueryKeyArgs & {
  options?: UseQueryOptions<CursorIndexedProcessRunsQueryState>;
}) => {
  return useQuery<CursorIndexedProcessRunsQueryState>(
    getIndexedProcessRunsQueryKey({
      workspaceId,
      processId,
      sortBy,
      sortOrder,
      search,
      from,
      size,
      processState,
      cursor,
    }),
    async () => {
      const args: IndexedProcessRunsQueryArgs = {
        sortBy,
        sortOrder,
        search,
        from,
        processState,
        size,
        cursor,
      };

      let response;
      if (processId) {
        response = await getSearchProcessRunsByProcess(workspaceId, processId, args);
      } else {
        response = await getSearchProcessRuns(workspaceId, args);
      }
      response = validateApiResponse(response);

      return {
        processRuns: response.data?.data ?? [],
        total: response.data?.total ?? 0,
        previousCursor: response.data?.previousCursor ?? null,
        nextCursor: response.data?.nextCursor ?? null,
      };
    },
    { keepPreviousData: true, refetchOnMount: true, refetchInterval: 10000, ...options },
  );
};

const getClosestRunsQueryKey = (args: ClosestRunsQueryKeyArgs) => [args];

export const useProcessRunsWithClosestRunNumberQuery = ({
  workspaceId,
  processId,
  runNo,
}: ClosestRunsQueryKeyArgs) => {
  return useQuery<IndexedProcessRunsQueryState>(
    getClosestRunsQueryKey({ workspaceId, processId, runNo }),
    async () => {
      const response = validateApiResponse(
        await getSearchProcessRunsWithClosestRunNumber(workspaceId, processId, { runNo }),
      );
      return {
        processRuns: response.data?.data ?? [],
        total: response.data?.total ?? 0,
      };
    },
  );
};
